import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../../features/auth/authSlice';
import axios from 'axios';
import api, { API_URL } from '../../services/api';
import { 
  User, 
  CreditCard, 
  Shield, 
  Eye, 
  EyeOff,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Trash2,
  Bell,
  Globe,
  Settings,
  MapPin,
  AlertTriangle,
  FileText,
  Upload,
  Download
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext';
import Navbar from '../../components/ALLJsx/Navbar';
import Footer from '../../components/ALLJsx/Footer';
import ThemeToggleSwitch from '../../components/ALLJsx/ThemeToggleSwitch';
import { useAccountMenuDarkMode } from '../../hooks/useAccountMenuDarkMode';
import '../AllCss/Profile.css';
import VisitUsTab from '../../components/ALLJsx/VisitUsTab';
import AddressAutocompleteInput from '../../components/ALLJsx/AddressAutocompleteInput';
import AddPaymentCardPanel from '../../components/ALLJsx/AddPaymentCardPanel';
import CardBrandIcon from '../../components/ALLJsx/CardBrandIcon';
import { useConfirm } from '../../contexts/ConfirmContext';
import ReferralShareSection from '../../components/ALLJsx/ReferralShareSection';
import { redirectToPublicHome } from '../../utils/clientLogout';

const PROFILE_TABS = ['personal', 'interestedIn', 'payment', 'documents', 'security', 'notifications', 'preferences', 'referrals'];

function resolveProfileTab(tab) {
  if (!tab) return 'personal';
  if (tab === 'visitUs') return 'interestedIn';
  return PROFILE_TABS.includes(tab) ? tab : 'personal';
}

function Profile() {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { t } = useLocale();
  const confirmDialog = useConfirm();
  const { accountMenuDark, toggleAccountMenuDark } = useAccountMenuDarkMode();
  const [searchParams] = useSearchParams();
  const initialCarId = searchParams.get('carId') || '';
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => resolveProfileTab(tabFromUrl));
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // Email verification state
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  
  // Personal Info State (includes additional contact in personal info)
  const [personalInfo, setPersonalInfo] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    address2: user?.address2 || '',
    city: user?.city || '',
    state: user?.state || '',
    countyName: user?.countyName || '',
    zipCode: user?.zipCode || '',
    additionalContactName: user?.preferences?.additionalContact?.name || '',
    additionalContactPhone: user?.preferences?.additionalContact?.phone || '',
  });

  // Update personalInfo when user data changes
  useEffect(() => {
    if (user) {
      setPersonalInfo({
        fullName: user.fullName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        address2: user.address2 || '',
        city: user.city || '',
        state: user.state || '',
        countyName: user.countyName || '',
        zipCode: user.zipCode || '',
        additionalContactName: user.preferences?.additionalContact?.name || '',
        additionalContactPhone: user.preferences?.additionalContact?.phone || '',
      });
    }
  }, [user]);

  useEffect(() => {
    setActiveTab(resolveProfileTab(tabFromUrl));
  }, [tabFromUrl]);

  const persistAuthUser = useCallback((updatedUser) => {
    if (!updatedUser) return;
    dispatch(updateUser({ user: updatedUser }));
  }, [dispatch]);

  // Payment Methods State
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingCards, setLoadingCards] = useState(false);
  const [paymentCardsError, setPaymentCardsError] = useState('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [viewingCardId, setViewingCardId] = useState(null);
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [cardAddress, setCardAddress] = useState({
    country: 'United States',
    address: '',
    address2: '',
    city: '',
    state: '',
    countyName: '',
    zipCode: '',
  });
  const [cardFormData, setCardFormData] = useState({
    nickname: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    zipCode: '',
    cardholderName: '',
  });

  // Automatically request verification code when modal opens
  useEffect(() => {
    if (showEmailVerificationModal && newEmailAddress && newEmailAddress !== user?.email) {
      handleRequestEmailChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailVerificationModal, newEmailAddress]);

  // Sync card address with personal info when checkbox is checked
  useEffect(() => {
    if (useSameAddress) {
      setCardAddress({
        address: personalInfo.address || '',
        address2: personalInfo.address2 || '',
        city: personalInfo.city || '',
        state: personalInfo.state || '',
        countyName: personalInfo.countyName || '',
        country: 'United States',
        zipCode: personalInfo.zipCode || '',
      });
    }
  }, [useSameAddress, personalInfo.address, personalInfo.address2, personalInfo.city, personalInfo.state, personalInfo.countyName, personalInfo.zipCode]);

  // Fetch payment cards from API (PostgreSQL payment_cards table via server)
  const fetchPaymentCards = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoadingCards(true);
    setPaymentCardsError('');
    try {
      const response = await api.get('/payment-cards');

      if (response.data.success) {
        const raw = response.data.data;
        const list = Array.isArray(raw) ? raw : [];
        const cards = list.map((card) => ({
          id: card.id,
          type: card.cardType || 'visa',
          last4: card.cardNumberLast4,
          expiry: `${String(card.expiryMonth).padStart(2, '0')}/${String(card.expiryYear).slice(-2)}`,
          isDefault: card.isDefault,
          requiresRevault: card.requiresRevault === true,
          nickname: card.nickname || null,
          address: card.address || null,
          address2: card.address2 || null,
          city: card.city || null,
          state: card.state || null,
          countyName: card.countyName || null,
          country: card.country || null,
          zipCode: card.zipCode || card.zipcode || null,
        }));
        setPaymentMethods(cards);
      } else {
        setPaymentCardsError(response.data.message || 'Could not load payment cards.');
      }
    } catch (error) {
      console.error('Error fetching payment cards:', error);
      const status = error.response?.status;
      if (status === 401) {
        setPaymentCardsError('Your session expired. Please sign in again.');
      } else if (!error.response) {
        setPaymentCardsError(
          `Cannot reach the API at ${API_URL}. Start the server (npm run dev in server/) and ensure REACT_APP_API_URL matches.`
        );
      } else {
        setPaymentCardsError(
          error.response?.data?.message || 'Failed to load payment cards. Please try again.'
        );
      }
      setPaymentMethods([]);
    } finally {
      setLoadingCards(false);
    }
  }, [isAuthenticated]);

  // Fetch cards when component mounts and user is authenticated
  useEffect(() => {
    if (activeTab === 'payment' && isAuthenticated) {
      fetchPaymentCards();
    }
  }, [activeTab, isAuthenticated, fetchPaymentCards]);

  // Additional Persons State
  const [additionalPersons, setAdditionalPersons] = useState([
    {
      id: 1,
      fullName: '',
      email: '',
      phone: '',
      relationship: '',
    }
  ]);

  // Documents State
  const MAX_DOCUMENT_PERSONS = 6;
  const GENERIC_PERSON_NAME_PATTERN = /^Person \d+$/i;

  const [expandedPersonForDocuments, setExpandedPersonForDocuments] = useState(null);
  const [documentPersons, setDocumentPersons] = useState([1, 2, 3, 4]); // Dynamic list of person numbers
  const [personDocuments, setPersonDocuments] = useState({
    1: [], // Person 1 documents
    2: [], // Person 2 documents
    3: [], // Person 3 documents
    4: [], // Person 4 documents
  });
  const [uploadingDocuments, setUploadingDocuments] = useState({});
  const [personNames, setPersonNames] = useState({
    1: 'Person 1',
    2: 'Person 2',
    3: 'Person 3',
    4: 'Person 4',
  });
  const [editingPersonName, setEditingPersonName] = useState(null);
  const [tempPersonName, setTempPersonName] = useState('');

  const getPersonDisplayName = (personNumber, index) => {
    const stored = personNames[personNumber]?.trim();
    if (stored && !GENERIC_PERSON_NAME_PATTERN.test(stored)) {
      return stored;
    }
    return `Person ${index + 1}`;
  };

  const isPersonCustomName = (personNumber, index) => {
    const stored = personNames[personNumber]?.trim();
    return Boolean(stored && !GENERIC_PERSON_NAME_PATTERN.test(stored));
  };

  const personHasUploadedDocuments = (personNumber) =>
    (personDocuments[personNumber]?.length ?? 0) > 0;

  // Add new person to documents (max 6 slots, reuses lowest free slot 1–6)
  const handleAddDocumentPerson = () => {
    if (documentPersons.length >= MAX_DOCUMENT_PERSONS) return;

    const nextPersonNumber = [1, 2, 3, 4, 5, 6].find((n) => !documentPersons.includes(n));
    if (!nextPersonNumber) return;

    const nextDisplayIndex = documentPersons.length + 1;
    setDocumentPersons([...documentPersons, nextPersonNumber].sort((a, b) => a - b));
    setPersonDocuments({
      ...personDocuments,
      [nextPersonNumber]: [],
    });
    setPersonNames({
      ...personNames,
      [nextPersonNumber]: `Person ${nextDisplayIndex}`,
    });
  };

  // Handle person name editing
  const handleStartEditingName = (personNumber, e) => {
    e.stopPropagation(); // Prevent expanding/collapsing when clicking name
    const index = documentPersons.indexOf(personNumber);
    setEditingPersonName(personNumber);
    setTempPersonName(getPersonDisplayName(personNumber, index));
  };

  const handleSavePersonName = async (personNumber, e) => {
    e.stopPropagation();
    if (!tempPersonName.trim()) {
      setEditingPersonName(null);
      setTempPersonName('');
      return;
    }

    const trimmedName = tempPersonName.trim();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/person-name`,
        {
          personNumber: personNumber,
          personName: trimmedName,
        },
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        setPersonNames({
          ...personNames,
          [personNumber]: trimmedName,
        });
        setEditingPersonName(null);
        setTempPersonName('');
      }
    } catch (error) {
      console.error('Error saving person name:', error);
      setSaveMessage('Failed to save person name. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleCancelEditingName = (personNumber, e) => {
    e.stopPropagation();
    setEditingPersonName(null);
    setTempPersonName('');
  };

  // Remove person from documents (but keep at least one; block if files exist)
  const handleRemoveDocumentPerson = (personNumber) => {
    if (documentPersons.length <= 1) return; // Don't allow removing the last person
    if (personHasUploadedDocuments(personNumber)) {
      setSaveMessage('Remove all documents from this person before removing the box.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    setDocumentPersons(documentPersons.filter((num) => num !== personNumber).sort((a, b) => a - b));
    const updatedDocuments = { ...personDocuments };
    delete updatedDocuments[personNumber];
    setPersonDocuments(updatedDocuments);

    setPersonNames((prev) => {
      const next = { ...prev };
      delete next[personNumber];
      return next;
    });

    // If the removed person was expanded, collapse it
    if (expandedPersonForDocuments === personNumber) {
      setExpandedPersonForDocuments(null);
    }
  };

  // Fetch documents for a specific person (using POST to avoid exposing personNumber in URL)
  const fetchPersonDocuments = useCallback(async (personNumber) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/documents/by-person`,
        { personNumber },
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        setPersonDocuments(prev => ({
          ...prev,
          [personNumber]: response.data.data.documents || [],
        }));
      }
    } catch (error) {
      // Don't show error if it's just no documents found
      if (error.response?.status !== 404) {
        console.error(`Error fetching documents for person ${personNumber}:`, error);
      }
    }
  }, []);

  // Fetch all documents (grouped by person) when documents tab is active
  const fetchAllDocuments = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await axios.get(`${API_URL}/api/documents`, {
        withCredentials: true,
      });

      if (response.data.success) {
        const docs = response.data.data.documents || [];
        const grouped = docs.reduce((acc, doc) => {
          const personNumber = doc.personNumber;
          if (!acc[personNumber]) acc[personNumber] = [];
          acc[personNumber].push(doc);
          return acc;
        }, {});

        setPersonDocuments((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            next[key] = grouped[parseInt(key, 10)] || [];
          });
          Object.entries(grouped).forEach(([key, value]) => {
            next[key] = value;
          });
          return next;
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching documents:', error);
      }
    }
  }, [isAuthenticated]);

  // Fetch person names when documents tab is active
  const fetchPersonNames = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/documents/person-names`,
        {
        withCredentials: true,
                  }
      );

      if (response.data.success && response.data.data.personNames) {
        const names = response.data.data.personNames;
        setPersonNames(prev => ({
          ...prev,
          ...names,
        }));
        setDocumentPersons((prev) => {
          const merged = new Set([
            ...prev,
            ...Object.keys(names).map((key) => parseInt(key, 10)),
          ]);
          return [...merged]
            .filter((n) => n >= 1 && n <= MAX_DOCUMENT_PERSONS)
            .sort((a, b) => a - b)
            .slice(0, MAX_DOCUMENT_PERSONS);
        });
      }
    } catch (error) {
      // Don't show error if it's just no names found
      if (error.response?.status !== 404) {
        console.error('Error fetching person names:', error);
      }
    }
  }, [isAuthenticated]);

  // Fetch person names and documents when documents tab is active
  useEffect(() => {
    if (activeTab === 'documents' && isAuthenticated) {
      fetchPersonNames();
      fetchAllDocuments();
    }
  }, [activeTab, isAuthenticated, fetchPersonNames, fetchAllDocuments]);

  // Fetch documents when a person section is expanded
  useEffect(() => {
    if (expandedPersonForDocuments && isAuthenticated) {
      fetchPersonDocuments(expandedPersonForDocuments);
    }
  }, [expandedPersonForDocuments, isAuthenticated, fetchPersonDocuments]);

  // Fetch 2FA status (defined before conditional return for React Hooks rules)
  const fetch2FAStatus = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        return;
      }
      const response = await axios.get(`${API_URL}/api/auth/profile/2fa/status`, {
        withCredentials: true,
      });
      if (response.data.success) {
        setTwoFactorEnabled(response.data.data.twoFactorEnabled || false);
      }
    } catch (error) {
      // Only log if it's not a 401 (unauthorized) - that's expected when not logged in
      if (error.response?.status !== 401) {
        console.error('Error fetching 2FA status:', error.response?.data?.message || error.message);
      }
    }
  }, [isAuthenticated]);

  // Fetch user preferences
  const fetchPreferences = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setPreferencesLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/auth/profile/preferences`, {
        withCredentials: true,
      });
      if (response.data.success && response.data.data.preferences) {
        setPreferences(response.data.data.preferences);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Don't show error if it's just no preferences set yet
      if (error.response?.status !== 404) {
        setSaveMessage('Failed to load preferences. Using defaults.');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } finally {
      setPreferencesLoading(false);
    }
  }, [isAuthenticated]);

  // Update preferences
  const handleUpdatePreferences = async (updatedPreferences) => {
    setPreferencesSaving(true);
    setSaveMessage('');
    try {
      const response = await axios.put(
        `${API_URL}/api/auth/profile/preferences`,
        { preferences: updatedPreferences },
        {
        withCredentials: true,
        }
      );
      if (response.data.success) {
        setPreferences(response.data.data.preferences);
        setSaveMessage('Preferences saved successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      setSaveMessage(error.response?.data?.message || 'Failed to save preferences. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setPreferencesSaving(false);
    }
  };

  // Handle preference change
  const handlePreferenceChange = (key, value) => {
    const updatedPreferences = { ...preferences, [key]: value };
    setPreferences(updatedPreferences);
    // Auto-save preferences
    handleUpdatePreferences(updatedPreferences);
  };

  // Fetch 2FA status when security tab is active (defined before conditional return for React Hooks rules)
  useEffect(() => {
    if (activeTab === 'security' && isAuthenticated) {
      fetch2FAStatus();
    }
  }, [activeTab, isAuthenticated, fetch2FAStatus]);

  // Fetch preferences when preferences or notifications tab is active
  useEffect(() => {
    if ((activeTab === 'preferences' || activeTab === 'notifications') && isAuthenticated) {
      fetchPreferences();
    }
  }, [activeTab, isAuthenticated, fetchPreferences]);

  // Handle file upload
  const handleFileUpload = async (personNumber, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setSaveMessage('Invalid file type. Only PDF, JPG, and PNG files are allowed.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setSaveMessage('File size too large. Maximum size is 10MB.');
      return;
    }

    setUploadingDocuments(prev => ({ ...prev, [personNumber]: true }));
    setSaveMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('personNumber', personNumber.toString());

      const response = await axios.post(
        `${API_URL}/api/documents/upload`,
        formData,
        {
        withCredentials: true,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // Refresh documents for this person
        await fetchPersonDocuments(personNumber);
        setSaveMessage('Document uploaded successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      setSaveMessage(
        error.response?.data?.message || 
        'Failed to upload document. Please try again.'
      );
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [personNumber]: false }));
    }
  };

  // Handle file selection
  const handleFileSelect = (personNumber, event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(personNumber, file);
    }
    // Reset input
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (personNumber, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(personNumber, file);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (personNumber, documentId) => {
    const ok = await confirmDialog({
      title: 'Delete document',
      description: 'Are you sure you want to delete this document?',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      const response = await axios.delete(
        `${API_URL}/api/documents/${documentId}`,
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        // Refresh documents for this person
        await fetchPersonDocuments(personNumber);
        setSaveMessage('Document deleted successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      setSaveMessage(
        error.response?.data?.message || 
        'Failed to delete document. Please try again.'
      );
    }
  };

  // Handle document download
  const handleDownloadDocument = (documentId, originalFileName) => {
    const url = `${API_URL}/api/documents/${documentId}/download`;
    
    // For authenticated downloads, we need to fetch with token
    axios.get(url, {
        withCredentials: true,
            responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', originalFileName);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    }).catch(error => {
      setSaveMessage('Failed to download document. Please try again.');
    });
  };

  // Security State
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorQRCode, setTwoFactorQRCode] = useState(null);
  const [twoFactorSecret, setTwoFactorSecret] = useState(null);
  const [twoFactorVerificationCode, setTwoFactorVerificationCode] = useState('');
  const [twoFactorVerificationCode2, setTwoFactorVerificationCode2] = useState('');
  const [twoFactorMessage, setTwoFactorMessage] = useState('');
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [showDisable2FAConfirm, setShowDisable2FAConfirm] = useState(false);
  const [disable2FAPassword, setDisable2FAPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Preferences State
  const [preferences, setPreferences] = useState({
    theme: 'light',
    language: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    emailNotifications: true,
    marketingEmails: false,
  });
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [preferencesSaving, setPreferencesSaving] = useState(false);

  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    
    // Always update the form field value as user types
    setPersonalInfo({
      ...personalInfo,
      [name]: value,
    });
  };

  // Handle email field blur or Enter key - trigger verification modal
  const handleEmailFieldBlur = (e) => {
    const newEmail = e.target.value.trim();
    
    // Only show modal if email is different and valid
    if (newEmail && newEmail !== user?.email && newEmail.includes('@')) {
      setNewEmailAddress(newEmail);
      setShowEmailVerificationModal(true);
      setVerificationCode('');
      setVerificationMessage('');
    }
  };

  // Handle Enter key on email field
  const handleEmailFieldKeyDown = (e) => {
    if (e.key === 'Enter') {
    e.preventDefault();
      const newEmail = e.target.value.trim();
      
      // Only show modal if email is different and valid
      if (newEmail && newEmail !== user?.email && newEmail.includes('@')) {
        setNewEmailAddress(newEmail);
        setShowEmailVerificationModal(true);
        setVerificationCode('');
        setVerificationMessage('');
      }
    }
  };

  // Request email change verification code
  const handleRequestEmailChange = async () => {
    if (!newEmailAddress || newEmailAddress === user?.email) {
      setVerificationMessage('Please enter a different email address');
      return;
    }

    setSendingCode(true);
    setVerificationMessage('');

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/profile/request-email-change`,
        { newEmail: newEmailAddress },
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        setVerificationMessage('Verification code has been sent to your new email address. Please check your inbox.');
      }
    } catch (error) {
      setVerificationMessage(error.response?.data?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  };


  // Verify email change code
  const handleVerifyEmailChange = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationMessage('Please enter a valid 6-digit code');
      return;
    }

    setVerifyingEmail(true);
    setVerificationMessage('');

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/profile/verify-email-change`,
        { verificationCode },
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        setVerificationMessage('Email address updated successfully!');
        // Update the email in personalInfo from response
        if (response.data.data?.user) {
          persistAuthUser(response.data.data.user);
          setPersonalInfo({
            ...personalInfo,
            email: response.data.data.user.email,
          });
        } else {
          // Fallback to newEmailAddress if response doesn't include user
          setPersonalInfo({
            ...personalInfo,
            email: newEmailAddress,
          });
        }
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowEmailVerificationModal(false);
          setVerificationCode('');
          setVerificationMessage('');
          setNewEmailAddress('');
        }, 2000);
      }
    } catch (error) {
      setVerificationMessage(
        error.response?.data?.message || 
        error.message || 
        'Failed to verify email change. Please try again.'
      );
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleSavePersonalInfo = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveMessage('');

    try {
      const { additionalContactName, additionalContactPhone, ...profileFields } = personalInfo;
      const payload = { ...profileFields };
      if (additionalContactName !== undefined) payload.additionalContactName = additionalContactName;
      if (additionalContactPhone !== undefined) payload.additionalContactPhone = additionalContactPhone;
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        payload,
        {
        withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );


      if (response.data.success) {
        if (response.data.data?.user) {
          persistAuthUser(response.data.data.user);
        }
        setSaveMessage('Profile updated successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error response:', error.response?.data);
      setSaveMessage(error.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultCard = async (cardId) => {
    try {
      const response = await api.patch(`/payment-cards/${cardId}/set-default`, {});

      if (response.data.success) {
        // Refresh the payment cards list
        await fetchPaymentCards();
      }
    } catch (error) {
      console.error('Error setting default card:', error);
      setSaveMessage(error.response?.data?.message || 'Failed to set default card. Please try again.');
    }
  };

  const handleRemoveCard = async (cardId) => {
    const ok = await confirmDialog({
      title: 'Remove payment card',
      description: 'Are you sure you want to remove this payment card?',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;

    try {
      const response = await api.delete(`/payment-cards/${cardId}`);

      if (response.data.success) {
        await fetchPaymentCards();
        setSaveMessage('Payment card removed successfully!');
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error removing card:', error);
      setSaveMessage(error.response?.data?.message || 'Failed to remove payment card. Please try again.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!passwords.current) {
      setSaveMessage('Please enter your current password');
      return;
    }
    
    if (!passwords.new || passwords.new.length < 6) {
      setSaveMessage('New password must be at least 6 characters long');
      return;
    }
    
    if (passwords.new !== passwords.confirm) {
      setSaveMessage('New passwords do not match');
      return;
    }

    setLoading(true);
    setSaveMessage('');

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/profile/change-password`,
        {
          currentPassword: passwords.current,
          newPassword: passwords.new,
        },
        {
        withCredentials: true,
                  }
      );

      if (response.data.success) {
        setSaveMessage('Password changed successfully!');
    setPasswords({ current: '', new: '', confirm: '' });
        // Clear message after 3 seconds
        setTimeout(() => setSaveMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setSaveMessage(
        error.response?.data?.message || 
        'Failed to change password. Please check your current password and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Generate 2FA secret and QR code
  const handleGenerate2FASecret = async () => {
    setTwoFactorLoading(true);
    setTwoFactorMessage('');
    try {
      if (!isAuthenticated) {
        setTwoFactorMessage('You must be logged in to enable 2FA');
        setTwoFactorLoading(false);
        return;
      }
      const response = await axios.post(`${API_URL}/api/auth/profile/2fa/generate-secret`, {}, {
        withCredentials: true,
      });
      if (response.data.success) {
        setTwoFactorQRCode(response.data.data.qrCode);
        setTwoFactorSecret(response.data.data.manualEntryKey);
        setShow2FASetup(true);
        setTwoFactorMessage('Scan the QR code with your authenticator app');
      }
    } catch (error) {
      console.error('Error generating 2FA secret:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate 2FA secret. Please try again.';
      setTwoFactorMessage(errorMessage);
      
      // Log detailed error for debugging
      if (error.response) {
        console.error('Response error:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('Request error:', error.request);
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Enable 2FA
  const handleEnable2FA = async () => {
    if (!twoFactorVerificationCode || twoFactorVerificationCode.length !== 6) {
      setTwoFactorMessage('Please enter the first 6-digit code');
      return;
    }
    if (!twoFactorVerificationCode2 || twoFactorVerificationCode2.length !== 6) {
      setTwoFactorMessage('Please enter the second 6-digit code');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMessage('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/profile/2fa/enable`, {
        code: twoFactorVerificationCode,
        code2: twoFactorVerificationCode2,
      }, {
        withCredentials: true,
      });
      if (response.data.success) {
        setTwoFactorEnabled(true);
        setShow2FASetup(false);
        setTwoFactorQRCode(null);
        setTwoFactorSecret(null);
        setTwoFactorVerificationCode('');
        setTwoFactorVerificationCode2('');
        setTwoFactorMessage('2FA enabled successfully!');
        setTimeout(() => setTwoFactorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      setTwoFactorMessage(error.response?.data?.message || 'Invalid verification codes. Please try again.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Disable 2FA
  const handleDisable2FA = async () => {
    if (!disable2FAPassword) {
      setTwoFactorMessage('Please enter your password to disable 2FA');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorMessage('');
    try {
      const response = await axios.post(`${API_URL}/api/auth/profile/2fa/disable`, {
        password: disable2FAPassword,
      }, {
        withCredentials: true,
      });
      if (response.data.success) {
        setTwoFactorEnabled(false);
        setShowDisable2FAConfirm(false);
        setDisable2FAPassword('');
        setTwoFactorMessage('2FA disabled successfully');
        setTimeout(() => setTwoFactorMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      setTwoFactorMessage(error.response?.data?.message || 'Failed to disable 2FA. Please check your password.');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // Handle account deletion
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setSaveMessage('Please enter your password to confirm account deletion.');
      return;
    }

    setDeletingAccount(true);
    setSaveMessage('');

    try {
      const response = await axios.delete(
        `${API_URL}/api/auth/profile`,
        {
        withCredentials: true,
                    data: {
            password: deletePassword,
          },
        }
      );

      if (response.data.success) {
        // Clear local storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Redirect to home page or login
        redirectToPublicHome();
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      setSaveMessage(
        error.response?.data?.message || 
        'Failed to delete account. Please check your password and try again.'
      );
      setDeletingAccount(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="profile-container">
          <div className="profile-grid">
            {/* Sidebar Navigation */}
            <aside className="profile-sidebar">
              <div className="sidebar-content">
                <div className="sidebar-header">
                  <Settings size={18} />
                  <h1 className="sidebar-title">Settings</h1>
                </div>
                <nav>
                  <ul className="sidebar-nav">
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'personal' ? 'active' : ''}`}
                        onClick={() => setActiveTab('personal')}
                      >
                        <User size={16} />
                        <span>{t('profile.personalInfo')}</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'interestedIn' ? 'active' : ''}`}
                        onClick={() => setActiveTab('interestedIn')}
                      >
                        <MapPin size={16} />
                        <span>Interested In</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'payment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('payment')}
                      >
                        <CreditCard size={16} />
                        <span>{t('profile.paymentMethods')}</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'documents' ? 'active' : ''}`}
                        onClick={() => setActiveTab('documents')}
                      >
                        <FileText size={16} />
                        <span>{t('profile.documents')}</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'security' ? 'active' : ''}`}
                        onClick={() => setActiveTab('security')}
                      >
                        <Shield size={16} />
                        <span>{t('profile.security')}</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        className={`sidebar-nav-button ${activeTab === 'notifications' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notifications')}
                      >
                        <Bell size={16} />
                        <span>{t('profile.notifications')}</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        type="button"
                        className={`sidebar-nav-button ${activeTab === 'referrals' ? 'active' : ''}`}
                        onClick={() => setActiveTab('referrals')}
                      >
                        <User size={16} />
                        <span>Referrals</span>
                      </button>
                    </li>
                    <li className="sidebar-nav-item">
                      <button
                        type="button"
                        className={`sidebar-nav-button ${activeTab === 'preferences' ? 'active' : ''}`}
                        onClick={() => setActiveTab('preferences')}
                      >
                        <Globe size={16} />
                        <span>{t('profile.preferences')}</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </aside>

          {/* Main Content */}
          <main className="min-h-screen">
            {/* Personal Information Tab */}
            {activeTab === 'personal' && (
              <div>
                <div className="py-8 px-12 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-900">{t('profile.personalInformation')}</h2>
                  <p className="text-sm text-neutral-600 mt-1">Update your personal details and contact information</p>
                </div>

                <div className="py-8 px-12">
                  <div className="max-w-2xl">
                    <div className="space-y-6">
                      {/* Line 1: Full Name and Email Address, Line 2: Phone Number below Full Name */}
                      <div className="grid grid-cols-2 gap-x-6">
                        <div className="space-y-6">
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="fullName"
                          value={personalInfo.fullName}
                          onChange={handlePersonalInfoChange}
                          className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={personalInfo.phone}
                          onChange={handlePersonalInfoChange}
                          className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                        />
                          </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={personalInfo.email}
                          onChange={handlePersonalInfoChange}
                            onBlur={handleEmailFieldBlur}
                            onKeyDown={handleEmailFieldKeyDown}
                          className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                        />
                      </div>
                      </div>

                      <AddressAutocompleteInput
                        id="profile-address"
                        label="Address"
                        value={{
                          address: personalInfo.address,
                          address2: personalInfo.address2,
                          city: personalInfo.city,
                          state: personalInfo.state,
                          countyName: personalInfo.countyName,
                          zipCode: personalInfo.zipCode,
                        }}
                        onChange={(patch) =>
                          setPersonalInfo((prev) => ({
                            ...prev,
                            ...patch,
                          }))
                        }
                        labelClassName="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2"
                        inputClassName="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                        placeholder="Start typing your address…"
                      />

                      {/* Additional contact (e.g. emergency or preferred contact) - saved with profile */}
                      <div className="mt-6 pt-6 border-t border-neutral-200">
                        <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">Additional contact</h3>
                        <p className="text-xs text-neutral-600 mb-4">Optional: emergency or preferred contact name and phone (saved with your profile).</p>
                        <div className="grid grid-cols-2 gap-x-6">
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              Contact name
                            </label>
                            <input
                              type="text"
                              name="additionalContactName"
                              value={personalInfo.additionalContactName || ''}
                              onChange={handlePersonalInfoChange}
                              placeholder="e.g. Spouse or relative"
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              Contact phone
                            </label>
                            <input
                              type="tel"
                              name="additionalContactPhone"
                              value={personalInfo.additionalContactPhone || ''}
                              onChange={handlePersonalInfoChange}
                              placeholder="(555) 000-0000"
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Additional Persons Section */}
                    <div className="mt-8 pt-8 border-t border-neutral-200">
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-1">
                            Additional Contacts
                          </h3>
                          <p className="text-xs text-neutral-600">Add co-buyers, family members, or authorized users</p>
                        </div>
                        <button
                          onClick={() => {
                            const newId = additionalPersons.length > 0 
                              ? Math.max(...additionalPersons.map(p => p.id)) + 1 
                              : 1;
                            setAdditionalPersons([
                              ...additionalPersons,
                              {
                                id: newId,
                                fullName: '',
                                email: '',
                                phone: '',
                                relationship: '',
                              }
                            ]);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 border border-neutral-300 text-neutral-700 text-xs font-medium uppercase tracking-wide hover:bg-neutral-50"
                        >
                          <Plus size={14} />
                          Add Person
                        </button>
                      </div>

                      <div className="space-y-6">
                        {additionalPersons.map((person, index) => (
                          <div key={person.id} className="p-6 bg-neutral-50 border border-neutral-200 rounded">
                            <div className="flex items-center justify-between mb-4">
                              <h4 className="text-sm font-medium text-neutral-900">
                                Person {index + 1}
                              </h4>
                              {additionalPersons.length > 1 && (
                                <button
                                  onClick={() => {
                                    setAdditionalPersons(additionalPersons.filter(p => p.id !== person.id));
                                  }}
                                  className="p-1 text-neutral-500 hover:text-red-600"
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Full Name
                                </label>
                                <input
                                  type="text"
                                  value={person.fullName}
                                  onChange={(e) => {
                                    const updated = additionalPersons.map(p =>
                                      p.id === person.id ? { ...p, fullName: e.target.value } : p
                                    );
                                    setAdditionalPersons(updated);
                                  }}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Relationship
                                </label>
                                <select
                                  value={person.relationship}
                                  onChange={(e) => {
                                    const updated = additionalPersons.map(p =>
                                      p.id === person.id ? { ...p, relationship: e.target.value } : p
                                    );
                                    setAdditionalPersons(updated);
                                  }}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                                >
                                  <option value="">Select relationship</option>
                                  <option value="co-buyer">Co-Buyer</option>
                                  <option value="spouse">Spouse</option>
                                  <option value="family-member">Family Member</option>
                                  <option value="authorized-user">Authorized User</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Email Address
                                </label>
                                <input
                                  type="email"
                                  value={person.email}
                                  onChange={(e) => {
                                    const updated = additionalPersons.map(p =>
                                      p.id === person.id ? { ...p, email: e.target.value } : p
                                    );
                                    setAdditionalPersons(updated);
                                  }}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Phone Number
                                </label>
                                <input
                                  type="tel"
                                  value={person.phone}
                                  onChange={(e) => {
                                    const updated = additionalPersons.map(p =>
                                      p.id === person.id ? { ...p, phone: e.target.value } : p
                                    );
                                    setAdditionalPersons(updated);
                                  }}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-neutral-200 flex flex-col items-center gap-3">
                      {saveMessage && (
                        <div className={`text-sm ${saveMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
                          {saveMessage}
                        </div>
                      )}
                      <button
                        onClick={handleSavePersonalInfo}
                        disabled={loading}
                        className="px-6 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'interestedIn' && (
              <VisitUsTab user={user} initialCarId={initialCarId} />
            )}

            {/* Payment Methods Tab */}
            {activeTab === 'payment' && (
              <div>
                <div className="py-8 px-12 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-900">{t('profile.paymentMethodsTitle')}</h2>
                  <p className="text-sm text-neutral-600 mt-1">Manage your credit and debit cards</p>
                </div>

                <div className="py-8 px-12">
              {paymentCardsError && (
                <div className="mb-4 p-3 rounded text-sm bg-red-50 text-red-800 border border-red-200">
                  {paymentCardsError}
                </div>
              )}
              {saveMessage && activeTab === 'payment' && (
                <div className={`mb-4 p-3 rounded text-sm ${saveMessage.includes('successfully') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {saveMessage}
                </div>
              )}
                  <div className="max-w-2xl space-y-4">
                    {loadingCards ? (
                      <div className="text-center py-8 text-neutral-600">Loading payment cards...</div>
                    ) : paymentMethods.length === 0 && !paymentCardsError ? (
                      <div className="text-center py-8 text-neutral-600">No payment cards added yet.</div>
                    ) : paymentMethods.length === 0 ? null : (
                      paymentMethods.map((card) => (
                      <div
                        key={card.id}
                        className={`p-4 border ${
                          card.isDefault ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <CardBrandIcon type={card.type} />
                          <div>
                            <div className="text-sm font-medium text-neutral-900">
                              {card.nickname || `${card.type.charAt(0).toUpperCase() + card.type.slice(1)} •••• ${card.last4}`}
                            </div>
                            <div className="text-sm text-neutral-600 font-mono tabular-nums">
                              •••• •••• •••• {card.last4}
                            </div>
                            <div className="text-xs text-neutral-500 mt-1">
                              Expires {card.expiry}
                              {card.requiresRevault && (
                                <span className="ml-2 text-amber-700 font-medium">
                                  — Re-add required (secure update)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          {card.isDefault ? (
                            <span className="px-3 py-1 bg-neutral-900 text-white text-xs font-medium uppercase tracking-wide">
                              Default
                            </span>
                          ) : (
                            <button
                              onClick={() => handleSetDefaultCard(card.id)}
                              disabled={card.requiresRevault}
                              className="px-3 py-1 border border-neutral-300 text-neutral-700 text-xs font-medium uppercase tracking-wide disabled:opacity-40"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => setViewingCardId(viewingCardId === card.id ? null : card.id)}
                            className="p-2 text-neutral-500 hover:text-neutral-900"
                            title="View Card Details"
                          >
                            {viewingCardId === card.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                          <button
                            onClick={() => handleRemoveCard(card.id)}
                            className="p-2 text-neutral-500 hover:text-neutral-900"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* View Card Details Dropdown */}
                      {viewingCardId === card.id && (
                        <div className="payment-card-details border-t border-neutral-200">
                          <div className="payment-card-details-panel bg-neutral-50 rounded">
                            <h4 className="payment-card-details-title">Card Details</h4>
                            <dl className="payment-card-details-list">
                              {card.nickname && (
                                <div className="payment-card-details-row">
                                  <dt className="payment-card-details-label">Nickname</dt>
                                  <dd className="payment-card-details-value">{card.nickname}</dd>
                                </div>
                              )}
                              <div className="payment-card-details-row">
                                <dt className="payment-card-details-label">Card Type</dt>
                                <dd className="payment-card-details-value capitalize">{card.type}</dd>
                              </div>
                              <div className="payment-card-details-row">
                                <dt className="payment-card-details-label">Card Number</dt>
                                <dd className="payment-card-details-value">•••• •••• •••• {card.last4}</dd>
                              </div>
                              <div className="payment-card-details-row">
                                <dt className="payment-card-details-label">Expiry Date</dt>
                                <dd className="payment-card-details-value">{card.expiry}</dd>
                              </div>
                              {card.address && (
                                <>
                                  <div className="payment-card-details-row">
                                    <dt className="payment-card-details-label">Street Address</dt>
                                    <dd className="payment-card-details-value">{card.address}</dd>
                                  </div>
                                  {card.address2 && (
                                    <div className="payment-card-details-row">
                                      <dt className="payment-card-details-label">Street Address 2</dt>
                                      <dd className="payment-card-details-value">{card.address2}</dd>
                                    </div>
                                  )}
                                  <div className="payment-card-details-row payment-card-details-row--inline">
                                    <dt className="payment-card-details-label">City</dt>
                                    <dd className="payment-card-details-value">{card.city}</dd>
                                    <dt className="payment-card-details-label">State</dt>
                                    <dd className="payment-card-details-value">{card.state}</dd>
                                  </div>
                                  <div className="payment-card-details-row payment-card-details-row--inline">
                                    {card.countyName && (
                                      <>
                                        <dt className="payment-card-details-label">County</dt>
                                        <dd className="payment-card-details-value">{card.countyName}</dd>
                                      </>
                                    )}
                                    <dt className="payment-card-details-label">Zip Code</dt>
                                    <dd className="payment-card-details-value">{card.zipCode}</dd>
                                    {card.country && (
                                      <>
                                        <dt className="payment-card-details-label">Country</dt>
                                        <dd className="payment-card-details-value">{card.country}</dd>
                                      </>
                                    )}
                                  </div>
                                </>
                              )}
                            </dl>
                          </div>
                        </div>
                      )}
                    </div>
                      ))
                    )}

                    <button
                      onClick={() => {
                        if (showAddCard) {
                          // Reset form when closing
                          setUseSameAddress(false);
                          setCardAddress({
                            address: '',
                            address2: '',
                            city: '',
                            state: '',
                            countyName: '',
                            zipCode: '',
                          });
                          setCardFormData({
                            nickname: '',
                            cardNumber: '',
                            expiryDate: '',
                            cvv: '',
                            zipCode: '',
                            cardholderName: '',
                          });
                        }
                        setShowAddCard(!showAddCard);
                      }}
                      className="w-full p-4 border border-dashed border-neutral-300 text-sm font-medium text-neutral-600 flex items-center justify-center gap-2"
                    >
                      <Plus size={16} />
                      Add New Card
                    </button>

                    {showAddCard && (
                      <AddPaymentCardPanel
                        cardholderNameDefault={personalInfo.fullName || user?.fullName || ''}
                        billingDefaults={cardAddress}
                        useSameAddress={useSameAddress}
                        personalInfo={personalInfo}
                        onUseSameAddressChange={setUseSameAddress}
                        loading={loading}
                        onCancel={() => {
                          setShowAddCard(false);
                          setUseSameAddress(false);
                        }}
                        onError={(msg) => setSaveMessage(msg)}
                        onSuccess={async (msg) => {
                          setSaveMessage(msg || 'Payment card added successfully!');
                          await fetchPaymentCards();
                          setShowAddCard(false);
                          setUseSameAddress(false);
                          setTimeout(() => setSaveMessage(''), 3000);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <div className="py-8 px-12 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-900">{t('profile.documentsTitle')}</h2>
                  <p className="text-sm text-neutral-600 mt-1">Manage your uploaded documents and files</p>
                </div>

                <div className="py-8 px-12">
                  <div className="max-w-2xl">
                    {/* Add Person Button */}
                    {documentPersons.length < MAX_DOCUMENT_PERSONS && (
                    <div className="mb-6 flex justify-end">
                      <button
                        onClick={handleAddDocumentPerson}
                        className="flex items-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 text-sm font-medium uppercase tracking-wide hover:bg-neutral-50"
                      >
                        <Plus size={16} />
                        Add Person
                      </button>
                    </div>
                    )}

                    <div className="space-y-4">
                      {/* Person Boxes */}
                      {documentPersons.map((personNumber, index) => {
                        const personName = getPersonDisplayName(personNumber, index);
                        const isCustomName = isPersonCustomName(personNumber, index);
                        return (
                        <div 
                          key={personNumber} 
                          className={`border-2 rounded-lg ${
                            isCustomName 
                              ? 'border-neutral-900' 
                              : 'border-dashed border-neutral-300'
                          }`}
                        >
                          {/* Person Header - Clickable */}
                          <div className="w-full p-4 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                            <div className="flex-1 flex items-center justify-between">
                              {editingPersonName === personNumber ? (
                                <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="text"
                                    value={tempPersonName}
                                    onChange={(e) => setTempPersonName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSavePersonName(personNumber, e);
                                      } else if (e.key === 'Escape') {
                                        handleCancelEditingName(personNumber, e);
                                      }
                                    }}
                                    autoFocus
                                    className="flex-1 px-2 py-1 text-sm font-semibold text-neutral-900 uppercase tracking-wide border border-neutral-300 focus:outline-none focus:border-neutral-900"
                                  />
                                  <button
                                    onClick={(e) => handleSavePersonName(personNumber, e)}
                                    className="p-1 text-green-600 hover:text-green-700"
                                    title="Save"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    onClick={(e) => handleCancelEditingName(personNumber, e)}
                                    className="p-1 text-neutral-500 hover:text-neutral-700"
                                    title="Cancel"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => {
                                    setExpandedPersonForDocuments(
                                      expandedPersonForDocuments === personNumber ? null : personNumber
                                    );
                                  }}
                                  className="flex-1 text-left flex items-center justify-between"
                                >
                                  <h3 
                                    className="text-sm font-semibold text-neutral-900 uppercase tracking-wide hover:text-neutral-600 cursor-pointer"
                                    onClick={(e) => handleStartEditingName(personNumber, e)}
                                    title="Click to edit name"
                                  >
                                    {personName}
                                  </h3>
                                  {expandedPersonForDocuments === personNumber ? (
                                    <ChevronUp size={20} className="text-neutral-400" />
                                  ) : (
                                    <ChevronDown size={20} className="text-neutral-400" />
                                  )}
                                </button>
                              )}
                            </div>
                            {documentPersons.length > 1 &&
                              editingPersonName !== personNumber &&
                              !personHasUploadedDocuments(personNumber) && (
                              <button
                                onClick={() => handleRemoveDocumentPerson(personNumber)}
                                className="ml-4 p-1 text-neutral-500 hover:text-red-600"
                                title="Remove Person"
                              >
                                <X size={18} />
                              </button>
                            )}
                          </div>

                          {/* Expanded Content */}
                          {expandedPersonForDocuments === personNumber && (
                            <div className="p-6 border-t border-dashed border-neutral-300 space-y-6">
                              {saveMessage && (
                                <div className={`p-3 rounded text-sm ${saveMessage.includes('successfully') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                                  {saveMessage}
                                </div>
                              )}
                              {/* {t('profile.uploadDocuments')} Section */}
                              <div className="p-6 bg-neutral-50 border border-neutral-200 rounded">
                                <h4 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">
                                  {t('profile.uploadDocuments')}
                                </h4>
                                <p className="text-xs text-neutral-600 mb-4">
                                  Upload your driver's license, insurance documents, and other required files.
                                </p>
                                <div 
                                  className="border-2 border-dashed border-neutral-300 rounded-lg p-8 text-center"
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleDrop(personNumber, e)}
                                >
                                  <input
                                    type="file"
                                    id={`file-input-${personNumber}`}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileSelect(personNumber, e)}
                                    className="hidden"
                                  />
                                  <Upload className="mx-auto mb-3 text-neutral-400" size={32} />
                                  <p className="text-sm text-neutral-600 mb-2">Drag and drop files here, or click to browse</p>
                                  <p className="text-xs text-neutral-500">PDF, JPG, PNG up to 10MB</p>
                                  <button 
                                    onClick={() => document.getElementById(`file-input-${personNumber}`).click()}
                                    disabled={uploadingDocuments[personNumber]}
                                    className="mt-4 px-4 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {uploadingDocuments[personNumber] ? t('profile.uploading') : t('profile.chooseFiles')}
                                  </button>
                                </div>
                              </div>

                              {/* {t('profile.yourDocuments')} Section */}
                              <div>
                                <h4 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">
                                  {t('profile.yourDocuments')}
                                </h4>
                                <div className="space-y-3">
                                  {personDocuments[personNumber] && personDocuments[personNumber].length > 0 ? (
                                    personDocuments[personNumber].map((doc) => {
                                      const uploadDate = doc.uploadDate 
                                        ? new Date(doc.uploadDate).toLocaleDateString('en-US', { 
                                            year: 'numeric', 
                                            month: 'short', 
                                            day: 'numeric' 
                                          })
                                        : 'N/A';
                                      
                                      return (
                                        <div key={doc.id} className="p-4 border border-neutral-200 flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <FileText className="text-neutral-400" size={20} />
                                            <div>
                                              <p className="text-sm font-medium text-neutral-900">
                                                {doc.originalFileName || 'Document'}
                                              </p>
                                              <p className="text-xs text-neutral-500">
                                                Uploaded on {uploadDate}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <button 
                                              onClick={() => handleDownloadDocument(doc.id, doc.originalFileName)}
                                              className="p-2 text-neutral-500 hover:text-neutral-900"
                                              title="Download"
                                            >
                                              <Download size={16} />
                                            </button>
                                            <button 
                                              onClick={() => handleDeleteDocument(personNumber, doc.id)}
                                              className="p-2 text-neutral-500 hover:text-red-600"
                                              title="Delete"
                                            >
                                              <Trash2 size={16} />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })
                                  ) : (
                                    <div className="text-center py-8 text-neutral-500 text-sm">
                                      No documents uploaded yet
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div>
                <div className="py-8 px-12 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-900">{t('profile.securitySettings')}</h2>
                  <p className="text-sm text-neutral-600 mt-1">Manage your password and two-factor authentication</p>
                </div>

                <div className="py-8 px-12">
                  <div className="max-w-2xl">
                    {/* Change Password */}
                    <div className="pb-8 border-b border-neutral-200">
                      <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-6">
                        Change Password
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                            Current Password
                          </label>
                          <div className="relative">
                            <input
                              type={showCurrentPassword ? 'text' : 'password'}
                              value={passwords.current}
                              onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                              className="w-full px-3 py-2 pr-10 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500"
                            >
                              {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showNewPassword ? 'text' : 'password'}
                              value={passwords.new}
                              onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                              className="w-full px-3 py-2 pr-10 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500"
                            >
                              {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                          <p className="text-xs text-neutral-500 mt-2">Minimum 8 characters with at least 1 number and 1 letter</p>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwords.confirm}
                            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                            className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                          />
                        </div>

                        {saveMessage && (
                          <div className={`pt-2 text-sm ${saveMessage.includes('successfully') ? 'text-green-600' : 'text-red-600'}`}>
                            {saveMessage}
                          </div>
                        )}
                        <div className="pt-4">
                          <button
                            onClick={handleChangePassword}
                            disabled={loading}
                            className="px-6 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? 'Updating...' : 'Update Password'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Two-Factor Authentication */}
                    <div className="pt-8">
                      <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">
                        Two-Factor Authentication
                      </h3>
                      <p className="text-sm text-neutral-600 mb-6">
                        Add an extra layer of security to your account using an authenticator app
                      </p>

                      {twoFactorMessage && (
                        <div className={`mb-6 p-3 rounded text-sm ${twoFactorMessage.includes('successfully') ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                          {twoFactorMessage}
                        </div>
                      )}

                      <div className="flex items-center justify-between p-4 bg-neutral-50 border border-neutral-200">
                        <div>
                          <div className="text-sm font-medium text-neutral-900">
                            Status: <span className={twoFactorEnabled ? 'text-green-700' : 'text-neutral-500'}>
                              {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                          <p className="text-xs text-neutral-600 mt-1">
                            {twoFactorEnabled
                              ? 'Your account is protected with 2FA'
                              : 'Enable for additional security'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          {!twoFactorEnabled ? (
                            <button
                              onClick={handleGenerate2FASecret}
                              disabled={twoFactorLoading}
                              className="px-4 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {twoFactorLoading ? 'Generating...' : 'Enable 2FA'}
                            </button>
                          ) : (
                            <button
                              onClick={() => setShowDisable2FAConfirm(true)}
                              disabled={twoFactorLoading}
                              className="px-4 py-2 border border-red-300 text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Disable 2FA
                            </button>
                          )}
                        </div>
                      </div>

                      {/* 2FA Setup - QR Code and Verification */}
                      {show2FASetup && !twoFactorEnabled && (
                        <div className="mt-6 p-6 bg-neutral-50 border border-neutral-200">
                          <h4 className="text-xs font-semibold text-neutral-900 uppercase tracking-wide mb-4">
                            Setup Two-Factor Authentication
                          </h4>
                          
                          {/* Download Instructions */}
                          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded">
                            <p className="text-xs font-medium text-primary-900 mb-2">
                              Step 1: Download an Authenticator App
                            </p>
                            <p className="text-xs text-primary-800 mb-3">
                              Install one of these free authenticator apps on your phone:
                            </p>
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-primary-700">•</span>
                                <span className="text-xs text-primary-800">
                                  <strong>Google Authenticator</strong> (Recommended)
                                  <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noopener noreferrer" className="ml-2 text-primary-600 underline">Android</a>
                                  {' | '}
                                  <a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">iOS</a>
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-primary-700">•</span>
                                <span className="text-xs text-primary-800">
                                  <strong>Microsoft Authenticator</strong>
                                  <a href="https://play.google.com/store/apps/details?id=com.azure.authenticator" target="_blank" rel="noopener noreferrer" className="ml-2 text-primary-600 underline">Android</a>
                                  {' | '}
                                  <a href="https://apps.apple.com/app/microsoft-authenticator/id983156458" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">iOS</a>
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-primary-700">•</span>
                                <span className="text-xs text-primary-800">
                                  <strong>Authy</strong>
                                  <a href="https://play.google.com/store/apps/details?id=com.authy.authy" target="_blank" rel="noopener noreferrer" className="ml-2 text-primary-600 underline">Android</a>
                                  {' | '}
                                  <a href="https://apps.apple.com/app/authy/id494168017" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline">iOS</a>
                                </span>
                              </div>
                            </div>
                          </div>

                          <p className="text-xs text-neutral-600 mb-6">
                            <strong>Step 2:</strong> Open the app and scan the QR code below, then enter <strong>two consecutive 6-digit codes</strong> from your authenticator app to verify
                          </p>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            {/* QR Code */}
                            <div className="border border-neutral-300 p-6 bg-white flex flex-col items-center justify-center">
                              {twoFactorQRCode ? (
                                <img src={twoFactorQRCode} alt="2FA QR Code" className="w-48 h-48 mb-3" />
                              ) : (
                                <div className="w-48 h-48 bg-neutral-100 mb-3 flex items-center justify-center">
                                  <span className="text-neutral-400 text-xs">Loading QR code...</span>
                                </div>
                              )}
                              <p className="text-xs text-neutral-600 text-center">Scan with authenticator app</p>
                            </div>

                            {/* Manual Entry */}
                            <div>
                              <h5 className="text-xs font-semibold text-neutral-900 uppercase tracking-wide mb-2">
                                Can't scan? Enter manually:
                              </h5>
                              {twoFactorSecret && (
                                <code className="block px-3 py-2 bg-white border border-neutral-200 text-xs font-mono text-neutral-900 break-all mb-4">
                                  {twoFactorSecret}
                                </code>
                              )}
                              <div className="mb-4">
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Enter first 6-digit code
                                </label>
                                <input
                                  type="text"
                                  value={twoFactorVerificationCode}
                                  onChange={(e) => setTwoFactorVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  placeholder="000000"
                                  maxLength={6}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900 text-center tracking-widest text-lg mb-3"
                                />
                              </div>
                              <div className="mb-4">
                                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                                  Enter second 6-digit code (wait for next code)
                                </label>
                                <input
                                  type="text"
                                  value={twoFactorVerificationCode2}
                                  onChange={(e) => setTwoFactorVerificationCode2(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                  placeholder="000000"
                                  maxLength={6}
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900 text-center tracking-widest text-lg"
                                />
                                <p className="text-xs text-neutral-500 mt-1">Enter the next code that appears in your authenticator app</p>
                              </div>
                              <button
                                onClick={handleEnable2FA}
                                disabled={twoFactorLoading || twoFactorVerificationCode.length !== 6 || twoFactorVerificationCode2.length !== 6}
                                className="w-full px-4 py-2 bg-neutral-900 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {twoFactorLoading ? 'Verifying...' : 'Verify and Enable'}
                              </button>
                              <button
                                onClick={() => {
                                  setShow2FASetup(false);
                                  setTwoFactorQRCode(null);
                                  setTwoFactorSecret(null);
                                  setTwoFactorVerificationCode('');
                                  setTwoFactorVerificationCode2('');
                                  setTwoFactorMessage('');
                                }}
                                className="w-full mt-2 px-4 py-2 border border-neutral-300 text-neutral-700 text-sm font-medium"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Disable 2FA Confirmation */}
                      {showDisable2FAConfirm && (
                        <div className="mt-6 p-6 bg-red-50 border border-red-200">
                          <h4 className="text-xs font-semibold text-red-900 uppercase tracking-wide mb-2">
                            Disable Two-Factor Authentication
                              </h4>
                          <p className="text-xs text-red-800 mb-4">
                            Please enter your password to confirm disabling 2FA
                          </p>
                          <div className="mb-4">
                            <label className="block text-xs font-medium text-red-900 uppercase tracking-wide mb-2">
                              Password
                            </label>
                            <input
                              type="password"
                              value={disable2FAPassword}
                              onChange={(e) => setDisable2FAPassword(e.target.value)}
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-red-300 focus:outline-none focus:border-red-500"
                            />
                              </div>
                          <div className="flex gap-3">
                            <button
                              onClick={handleDisable2FA}
                              disabled={twoFactorLoading || !disable2FAPassword}
                              className="px-4 py-2 bg-red-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {twoFactorLoading ? 'Disabling...' : 'Disable 2FA'}
                            </button>
                            <button
                              onClick={() => {
                                setShowDisable2FAConfirm(false);
                                setDisable2FAPassword('');
                                setTwoFactorMessage('');
                              }}
                              disabled={twoFactorLoading}
                              className="px-4 py-2 border border-red-300 text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Cancel
                              </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Delete Account - Danger Zone */}
                    <div className="pt-8 mt-8 border-t border-red-200">
                      <h3 className="text-sm font-semibold text-red-900 uppercase tracking-wide mb-2">
                        Danger Zone
                      </h3>
                      <p className="text-sm text-neutral-600 mb-6">
                        Once you delete your account, there is no going back. Please be certain.
                      </p>

                      {!showDeleteConfirm ? (
                        <div className="p-6 bg-red-50 border border-red-200 rounded">
                          <div className="flex items-start gap-4">
                            <AlertTriangle className="text-red-600 mt-0.5" size={20} />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-red-900 mb-2">
                                Delete Account
                              </h4>
                              <p className="text-xs text-red-800 mb-4">
                                This will permanently delete your account and all associated data. This action cannot be undone.
                              </p>
                              <button
                                onClick={() => setShowDeleteConfirm(true)}
                                className="px-6 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                              >
                                Delete My Account
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-6 bg-red-50 border-2 border-red-300 rounded">
                          <div className="flex items-start gap-4 mb-4">
                            <AlertTriangle className="text-red-600 mt-0.5" size={20} />
                            <div className="flex-1">
                              <h4 className="text-sm font-semibold text-red-900 mb-2">
                                Confirm Account Deletion
                              </h4>
                              <p className="text-xs text-red-800 mb-4">
                                To confirm, please enter your password. This action is permanent and cannot be undone.
                              </p>
                              <div className="mb-4">
                                <label className="block text-xs font-medium text-red-900 uppercase tracking-wide mb-2">
                                  Enter Your Password
                                </label>
                                <input
                                  type="password"
                                  value={deletePassword}
                                  onChange={(e) => setDeletePassword(e.target.value)}
                                  placeholder="Enter your password to confirm"
                                  className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-red-300 focus:outline-none focus:border-red-500"
                                />
                              </div>
                              <div className="flex gap-3">
                                <button
                                  onClick={handleDeleteAccount}
                                  disabled={!deletePassword || deletingAccount}
                                  className="px-6 py-2 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {deletingAccount ? 'Deleting...' : 'Yes, Delete My Account'}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowDeleteConfirm(false);
                                    setDeletePassword('');
                                  }}
                                  disabled={deletingAccount}
                                  className="px-6 py-2 border border-red-300 text-red-700 text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div>
                <div className="py-8 px-12 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold text-neutral-900">Notification Settings</h2>
                  <p className="text-sm text-neutral-600 mt-1">Manage how you receive notifications</p>
                </div>

                <div className="py-8 px-12">
                  <div className="max-w-2xl space-y-6">
                    {preferencesLoading ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-neutral-600">{t('profile.loadingPreferences')}</p>
                      </div>
                    ) : (
                      <>
                        <div className="mb-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <label htmlFor="notifications-email" className="block text-sm font-medium text-neutral-900 mb-1">
                                Email Notifications
                              </label>
                              <p className="text-xs text-neutral-600">
                                Account activity, appointments, and important updates
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                id="notifications-email"
                                checked={preferences.emailNotifications}
                                onChange={(e) => handlePreferenceChange('emailNotifications', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-neutral-900 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-neutral-900"></div>
                            </label>
                          </div>
                        </div>

                        <div className="mb-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <label htmlFor="notifications-marketing" className="block text-sm font-medium text-neutral-900 mb-1">
                                Marketing Emails
                              </label>
                              <p className="text-xs text-neutral-600">
                                Promotions, special offers, and new features
                              </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                id="notifications-marketing"
                                checked={preferences.marketingEmails}
                                onChange={(e) => handlePreferenceChange('marketingEmails', e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-neutral-900 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-neutral-900"></div>
                            </label>
                          </div>
                        </div>

                        {saveMessage && (
                          <div className={`p-4 rounded-lg ${
                            saveMessage.includes('success')
                              ? 'bg-green-50 text-green-800 border border-green-200'
                              : 'bg-red-50 text-red-800 border border-red-200'
                          }`}>
                            <p className="text-sm">{saveMessage}</p>
                          </div>
                        )}

                        {preferencesSaving && (
                          <div className="p-4 rounded-lg bg-primary-50 text-primary-800 border border-primary-200">
                            <p className="text-sm">Saving notification preferences...</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'referrals' && (
              <div className="profile-tab-content">
                <ReferralShareSection />
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className={`profile-preferences-panel${accountMenuDark ? ' profile-preferences-panel--dark' : ''}`}>
                <div className="py-8 px-12 border-b border-neutral-200 profile-preferences-header">
                  <h2 className="text-lg font-semibold text-neutral-900 profile-preferences-title">{t('profile.preferencesTitle')}</h2>
                  <p className="text-sm text-neutral-600 mt-1 profile-preferences-subtitle">{t('profile.customizeExperience')}</p>
                </div>

                <div className="py-8 px-12">
                  <div className="max-w-2xl space-y-8">
                    {preferencesLoading ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-neutral-600">{t('profile.loadingPreferences')}</p>
                      </div>
                    ) : (
                      <>
                        {/* Appearance Section — menu dark mode (local only, not site-wide) */}
                        <div className="border-b border-neutral-200 pb-6 profile-preferences-section">
                          <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">{t('profile.appearance')}</h3>
                          <p className="text-sm text-neutral-600 mb-4 profile-preferences-hint">
                            Dark mode applies to your account menu and this settings panel only.
                          </p>
                          <div className="profile-appearance-toggle">
                            <ThemeToggleSwitch
                              isDark={accountMenuDark}
                              onToggle={toggleAccountMenuDark}
                            />
                          </div>
                        </div>

                        {/* Language & Region Section */}
                        <div className="border-b border-neutral-200 pb-6 profile-preferences-section">
                          <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-4">{t('profile.languageAndRegion')}</h3>
                          
                          {/* Language */}
                          <div className="mb-6">
                            <label htmlFor="language" className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              {t('profile.language')}
                            </label>
                            <select
                              id="language"
                              value={preferences.language}
                              onChange={(e) => handlePreferenceChange('language', e.target.value)}
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            >
                              <option value="en">English</option>
                              <option value="es">Spanish</option>
                              <option value="fr">French</option>
                              <option value="de">German</option>
                              <option value="it">Italian</option>
                              <option value="pt">Portuguese</option>
                              <option value="zh">Chinese</option>
                              <option value="ja">Japanese</option>
                              <option value="ko">Korean</option>
                            </select>
                          </div>

                          {/* Timezone */}
                          <div className="mb-6">
                            <label htmlFor="timezone" className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              {t('profile.timezone')}
                            </label>
                            <select
                              id="timezone"
                              value={preferences.timezone}
                              onChange={(e) => handlePreferenceChange('timezone', e.target.value)}
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            >
                              <option value="America/New_York">Eastern Time (ET)</option>
                              <option value="America/Chicago">Central Time (CT)</option>
                              <option value="America/Denver">Mountain Time (MT)</option>
                              <option value="America/Los_Angeles">Pacific Time (PT)</option>
                              <option value="America/Phoenix">Arizona Time (MST)</option>
                              <option value="America/Anchorage">Alaska Time (AKST)</option>
                              <option value="Pacific/Honolulu">Hawaii Time (HST)</option>
                              <option value="UTC">UTC</option>
                              <option value="Europe/London">London (GMT)</option>
                              <option value="Europe/Paris">Paris (CET)</option>
                              <option value="Asia/Tokyo">Tokyo (JST)</option>
                              <option value="Asia/Shanghai">Shanghai (CST)</option>
                              <option value="Australia/Sydney">Sydney (AEST)</option>
                            </select>
                          </div>

                          {/* Date Format */}
                          <div className="mb-6">
                            <label htmlFor="dateFormat" className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              {t('profile.dateFormat')}
                            </label>
                            <select
                              id="dateFormat"
                              value={preferences.dateFormat}
                              onChange={(e) => handlePreferenceChange('dateFormat', e.target.value)}
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            >
                              <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</option>
                              <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</option>
                              <option value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</option>
                              <option value="DD MMM YYYY">DD MMM YYYY (31 Dec 2023)</option>
                            </select>
                          </div>

                          {/* Currency */}
                          <div className="mb-6">
                            <label htmlFor="currency" className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                              {t('profile.currency')}
                            </label>
                            <select
                              id="currency"
                              value={preferences.currency}
                              onChange={(e) => handlePreferenceChange('currency', e.target.value)}
                              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 focus:outline-none focus:border-neutral-900"
                            >
                              <option value="USD">USD - US Dollar ($)</option>
                              <option value="EUR">EUR - Euro (€)</option>
                              <option value="GBP">GBP - British Pound (£)</option>
                              <option value="CAD">CAD - Canadian Dollar (C$)</option>
                              <option value="AUD">AUD - Australian Dollar (A$)</option>
                              <option value="JPY">JPY - Japanese Yen (¥)</option>
                              <option value="CNY">CNY - Chinese Yuan (¥)</option>
                            </select>
                          </div>
                        </div>

                        {/* Save Message */}
                        {saveMessage && (
                          <div className={`p-4 rounded-lg ${
                            saveMessage.includes('success') 
                              ? 'bg-green-50 text-green-800 border border-green-200' 
                              : 'bg-red-50 text-red-800 border border-red-200'
                          }`}>
                            <p className="text-sm">{saveMessage}</p>
                          </div>
                        )}

                        {preferencesSaving && (
                          <div className="p-4 rounded-lg bg-primary-50 text-primary-800 border border-primary-200">
                            <p className="text-sm">Saving preferences...</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </main>
          </div>
      </div>

      {/* Email Verification Modal */}
      {showEmailVerificationModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-4"
          onClick={() => setShowEmailVerificationModal(false)}
        >
          <div 
            className="bg-white rounded-xl p-8 max-w-md w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-neutral-900">Verify Email Change</h3>
              <button
                onClick={() => {
                  setShowEmailVerificationModal(false);
                  setVerificationCode('');
                  setVerificationMessage('');
                  setNewEmailAddress('');
                  // Reset email field to original
                  setPersonalInfo({
                    ...personalInfo,
                    email: user?.email || '',
                  });
                }}
                className="text-neutral-500 hover:text-neutral-900"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-neutral-600 mb-4">
                We've sent a 6-digit verification code to <strong>{newEmailAddress}</strong>. 
                Please enter the code below to complete the email change.
              </p>

              {verificationMessage && (
                <div className={`p-3 rounded-lg mb-4 text-sm ${
                  verificationMessage.includes('successfully') 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {verificationMessage}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-xs font-medium text-neutral-700 uppercase tracking-wide mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                    setVerificationCode(value);
                  }}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest text-neutral-900 bg-white border-2 border-neutral-300 rounded-lg focus:outline-none focus:border-neutral-900"
                />
                <p className="text-xs text-neutral-500 mt-2 text-center">
                  Enter the 6-digit code sent to your new email
                </p>
              </div>

              {!verificationCode && (
                <button
                  onClick={handleRequestEmailChange}
                  disabled={sendingCode}
                  className="w-full px-4 py-2 bg-neutral-900 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingCode ? 'Sending...' : 'Resend Code'}
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowEmailVerificationModal(false);
                  setVerificationCode('');
                  setVerificationMessage('');
                  setNewEmailAddress('');
                  // Reset email field to original
                  setPersonalInfo({
                    ...personalInfo,
                    email: user?.email || '',
                  });
                }}
                className="flex-1 px-4 py-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-lg hover:bg-neutral-50"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyEmailChange}
                disabled={verificationCode.length !== 6 || verifyingEmail}
                className="flex-1 px-4 py-2 bg-neutral-900 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyingEmail ? 'Verifying...' : 'Verify & Update Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default Profile;